<template>
  <div class="fr-page">
    <!-- 顶部 -->
    <div class="fr-top">
      <div class="fr-top-l">
        <div class="fr-title">
          <el-button icon="ArrowLeft" text @click="back">返回</el-button>
          <h2>回收站</h2>
          <span class="fr-badge">{{ pageTotal }} 张</span>
        </div>
        <p class="fr-sub">
          删除的表单会先落到这里 —— <b>提交数据一并保留</b>，恢复后数据完全对得上。
          彻底删除才会连同提交记录与触发日志一起清除，且不可恢复。
        </p>
      </div>
      <div class="fr-top-r">
        <el-button
            :disabled="!selectedIds.length || operating"
            type="primary"
            icon="RefreshLeft"
            @click="batchRestore">批量恢复</el-button>
        <el-button
            :disabled="!selectedIds.length || operating"
            icon="Delete"
            class="fr-danger"
            @click="batchPurge">批量彻底删除</el-button>
      </div>
    </div>

    <!-- 筛选 -->
    <div class="fr-bar">
      <el-input
          v-model="query.keyword"
          clearable
          placeholder="搜索 表单标题 / 短码"
          prefix-icon="Search"
          class="fr-keyword"
          @keyup.enter="search"></el-input>
      <el-button icon="Search" @click="search">查询</el-button>
      <el-button icon="RefreshLeft" @click="reset">重置</el-button>
      <span class="fr-tip">
        <el-icon><InfoFilled /></el-icon>
        只会看到「已删除」的表单；列表按删除时间倒序。
      </span>
    </div>

    <!-- 列表 -->
    <div class="fr-card">
      <el-table
          v-loading="loading"
          :data="tableData"
          :cell-style="{padding: '8px 0'}"
          row-key="id"
          class="fr-table"
          @selection-change="onSelChange">
        <el-table-column type="selection" width="44"/>
        <el-table-column min-width="260" label="表单">
          <template #default="scope">
            <div class="fr-main">
              <el-image
                  v-if="scope.row.coverUrl || scope.row.bannerUrl"
                  :src="scope.row.coverUrl || scope.row.bannerUrl"
                  fit="cover"
                  class="fr-thumb">
                <template #error>
                  <div class="fr-thumb fr-thumb--empty"><el-icon><Picture /></el-icon></div>
                </template>
              </el-image>
              <div v-else class="fr-thumb fr-thumb--empty"><el-icon><Picture /></el-icon></div>
              <div class="fr-main-txt">
                <div class="fr-main-title" :title="scope.row.title">{{ scope.row.title }}</div>
                <div class="fr-main-meta">
                  <span v-if="scope.row.formKey" class="fr-code">{{ scope.row.formKey }}</span>
                  <span>{{ scope.row.fieldCount || 0 }} 个字段</span>
                  <span v-if="scope.row.groupName">{{ scope.row.groupName }}</span>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column width="130" label="已收集数据">
          <template #default="scope">
            <div class="fr-num" :class="{ 'is-zero': !scope.row.submitCount }">
              {{ scope.row.submitCount || 0 }} <em>条</em>
            </div>
            <div class="fr-num-sub">浏览 {{ scope.row.viewCount || 0 }}</div>
          </template>
        </el-table-column>

        <el-table-column width="110" label="原状态">
          <template #default="scope">
            <el-tag size="small" effect="plain">{{ statusText(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column width="180" label="删除时间">
          <template #default="scope">{{ scope.row.updateTime || '—' }}</template>
        </el-table-column>

        <el-table-column width="190" align="center" fixed="right" label="操作">
          <template #default="scope">
            <el-button size="small" text type="primary" :disabled="operating" @click="restore(scope.row)">恢复</el-button>
            <el-button size="small" text class="fr-danger-tx" :disabled="operating" @click="purge(scope.row)">彻底删除</el-button>
          </template>
        </el-table-column>

        <!-- 空态出口：有搜索词给「清除搜索」，否则给「返回表单管理」，不留死页 -->
        <template #empty>
          <div class="fr-empty">
            <el-icon><FolderOpened /></el-icon>
            <p>{{ query.keyword ? '没有匹配的表单，换个关键词试试' : '回收站是空的，删掉的表单会出现在这里' }}</p>
            <el-button v-if="query.keyword" text type="primary" @click="reset">清除搜索</el-button>
            <el-button v-else text type="primary" @click="back">返回表单管理</el-button>
          </div>
        </template>
      </el-table>

      <div class="pagination">
        <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :page-sizes="[10, 20, 50, 100]"
            :current-page="query.pageIndex"
            :page-size="query.pageSize"
            :total="pageTotal"
            @size-change="onSizeChange"
            @current-change="onPageChange"></el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { formRecyclePage, formRecycleRestore, formRecyclePurge, FORM_STATUS_TEXT } from '@/api/form';

export default {
  name: 'FormRecycle',
  data() {
    return {
      loading: false,
      operating: false,
      tableData: [],
      pageTotal: 0,
      selectedIds: [],
      query: {
        pageIndex: 1,
        pageSize: 10,
        keyword: '',
      },
    };
  },
  methods: {
    statusText(status) {
      return FORM_STATUS_TEXT[status] || '未知';
    },
    back() {
      const p = this.$router.push({ path: '/formList' });
      if (p && typeof p.catch === 'function') {
        p.catch(err => this.$message.error(`返回失败：${(err && err.message) || '未知错误'}`));
      }
    },
    onSelChange(rows) {
      this.selectedIds = (rows || []).map(r => r.id);
    },
    search() {
      this.query.pageIndex = 1;
      this.load();
    },
    reset() {
      this.query.keyword = '';
      this.query.pageIndex = 1;
      this.load();
    },
    onSizeChange(size) {
      this.query.pageSize = size;
      this.query.pageIndex = 1;
      this.load();
    },
    onPageChange(index) {
      this.query.pageIndex = index;
      this.load();
    },
    load() {
      this.loading = true;
      formRecyclePage(this.query).then(val => {
        this.loading = false;
        if (val && val.code === 200) {
          this.tableData = (val.data && val.data.records) || [];
          this.pageTotal = (val.data && val.data.total) || 0;
          this.selectedIds = [];
        } else {
          this.$message.warning((val && val.message) || '获取回收站失败');
        }
      }).catch(() => {
        this.loading = false;
      });
    },
    restore(row) {
      this.operating = true;
      formRecycleRestore(row.id).then(val => {
        this.operating = false;
        if (val && val.code === 200) {
          this.$message.success('已恢复，回到「表单管理」列表里');
          this.load();
        } else {
          this.$message.warning((val && val.message) || '恢复失败');
        }
      }).catch(() => {
        this.operating = false;
        this.$message.error('操作失败');
      });
    },
    batchRestore() {
      const ids = [...this.selectedIds];
      if (!ids.length) return;
      this.operating = true;
      formRecycleRestore(ids).then(val => {
        this.operating = false;
        if (val && val.code === 200) {
          this.$message.success('已恢复选中的表单');
          this.load();
        } else {
          this.$message.warning((val && val.message) || '恢复失败');
        }
      }).catch(() => {
        this.operating = false;
        this.$message.error('操作失败');
      });
    },
    purge(row) {
      const n = Number(row.submitCount) || 0;
      const extra = n > 0
          ? `\n\n⚠️ 该表单下还有 ${n} 条提交数据，会一并被永久删除。`
          : '';
      this.$confirm(
          `彻底删除「${row.title}」？此操作不可恢复。${extra}`,
          '高风险操作',
          { type: 'error', confirmButtonText: '确认彻底删除', cancelButtonText: '取消' },
      ).then(() => {
        this.operating = true;
        formRecyclePurge(row.id).then(val => {
          this.operating = false;
          if (val && val.code === 200) {
            this.$message.success('已彻底删除');
            this.load();
          } else {
            this.$message.warning((val && val.message) || '删除失败');
          }
        }).catch(() => {
          this.operating = false;
          this.$message.error('操作失败');
        });
      }).catch(() => {});
    },
    batchPurge() {
      const ids = [...this.selectedIds];
      if (!ids.length) return;
      // 统计一下会被一并删掉的提交数据量，避免用户对影响面没概念
      let submitTotal = 0;
      this.tableData.forEach(r => {
        if (ids.indexOf(r.id) >= 0) submitTotal += Number(r.submitCount) || 0;
      });
      const extra = submitTotal > 0 ? `\n\n⚠️ 这些表单下共 ${submitTotal} 条提交数据会一并被永久删除。` : '';
      this.$confirm(
          `彻底删除选中的 ${ids.length} 张表单？此操作不可恢复。${extra}`,
          '高风险操作',
          { type: 'error', confirmButtonText: '确认彻底删除', cancelButtonText: '取消' },
      ).then(() => {
        this.operating = true;
        formRecyclePurge(ids).then(val => {
          this.operating = false;
          if (val && val.code === 200) {
            this.$message.success('已彻底删除');
            this.load();
          } else {
            this.$message.warning((val && val.message) || '删除失败');
          }
        }).catch(() => {
          this.operating = false;
          this.$message.error('操作失败');
        });
      }).catch(() => {});
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在 9 个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则 9 份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单 9 页」负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.fr-page {
  /* —— Element 主色对齐「焱火绿」——
     此前后台从未覆盖 --el-color-primary，Element 一直是默认**蓝色**，
     而页面自定义元素用 #8dc63f ⇒ 同屏「按钮蓝 + 品牌绿」两个颜色。
     这 7 个变量一改，本页所有 el-button / el-switch / el-radio / el-checkbox /
     el-slider / el-pagination / focus 描边… 一次性全部变绿。
     light-3/5/7/8/9 与 dark-2 必须一并给出，否则 hover / disabled 态会
     回退到 Element 基于**蓝色**算出的浅色，出现「绿底 + 蓝边」的跳变。 */
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶；置成令牌后同类文字不会再各写一个灰） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */
.fr-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

.fr-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 14px 20px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fr-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fr-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fr-badge {
  padding: 1px 9px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
}

.fr-sub {
  margin: 6px 0 0;
  max-width: 720px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fr-sub b {
  color: var(--yh-t2);
}

.fr-top-r {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}

.fr-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 12px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fr-keyword {
  width: 250px;
}

.fr-tip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  font-size: 12px;
  color: var(--yh-t4);
}

.fr-card {
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 6px 12px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fr-table :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.fr-table :deep(.el-table__header th) {
  background: var(--yh-fill);
  color: var(--yh-t2);
  font-weight: 600;
}

.fr-table :deep(.el-table__row:hover > td) {
  background: var(--yh-fill) !important;
}

.fr-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.fr-thumb {
  flex: 0 0 auto;
  width: 56px;
  height: 42px;
  border-radius: var(--yh-r);
  border: 1px solid var(--yh-line);
  overflow: hidden;
  filter: grayscale(0.75);
  opacity: 0.85;
}

.fr-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: #dcdfe6;
  background: var(--yh-fill);
}

.fr-main-txt {
  flex: 1 1 0;
  min-width: 0;
}

.fr-main-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--yh-t2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fr-main-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 3px;
  font-size: 12px;
  color: var(--yh-t4);
}

.fr-code {
  font-family: Consolas, Monaco, monospace;
}

.fr-num {
  font-size: 15px;
  font-weight: 600;
  color: #e6a23c;
}

.fr-num.is-zero {
  color: var(--yh-t5);
}

.fr-num em {
  font-style: normal;
  font-size: 12px;
  font-weight: 400;
  color: var(--yh-t4);
}

.fr-num-sub {
  font-size: 11px;
  color: var(--yh-t5);
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

.fr-danger {
  color: #f56c6c;
  border-color: #fbc4c4;
}

.fr-danger:hover {
  background: #fef0f0;
  border-color: #f56c6c;
  color: #f56c6c;
}

.fr-danger-tx {
  color: #f56c6c;
}

/* 表格空态：图标 + 文案 + 出口按钮 */
.fr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 22px 0 8px;
  color: var(--yh-t5);
}

.fr-empty .el-icon {
  font-size: 30px;
  color: #dcdfe6;
}

.fr-empty p {
  margin: 0;
  font-size: 13px;
  color: var(--yh-t4);
}
</style>
